import type { TimelineTrackItem } from "./TimelineTrackItem";
import { clampWindow, fitWindow, spanOf, type TimeWindow } from "./window";

/**
 * Where a clip sits, as fractions of the window: `left` and `width` are in
 * `0..1` of the window's span, so the renderer draws them with CSS `%` and
 * no pixel measurement is needed to position anything. `left` is unclipped —
 * a clip that starts before the window has a negative `left`.
 */
export interface Placement {
  track: number;
  left: number;
  width: number;
}

/** One item's bar. */
export interface Clip<T> extends Placement {
  kind: "clip";
  item: TimelineTrackItem<T>;
}

/** Several items whose bars would overlap at the current zoom, drawn as one badge. */
export interface ClusterClip<T> extends Placement {
  kind: "cluster";
  /** Stable across re-layout: the track plus the first item's id. */
  id: string;
  items: readonly TimelineTrackItem<T>[];
  /** Earliest start among `items`. */
  startTime: number;
  /** Latest end among `items`. */
  endTime: number;
}

/** What `TimelineTracks` draws and `onSelect` receives. */
export type TrackClip<T> = Clip<T> | ClusterClip<T>;

/** `Map<item id, track index>` — which horizontal lane each item sits on. */
export type TrackById = ReadonlyMap<string, number>;

export interface TrackAssignment {
  trackById: TrackById;
  trackCount: number;
}

/**
 * Sort into `compareItems` order — the order every layout function expects.
 * A total order keeps clip and cluster identities stable across re-layout.
 */
export function sortItems<T>(
  items: readonly TimelineTrackItem<T>[],
): TimelineTrackItem<T>[] {
  return [...items].sort(compareItems);
}

/** What `compareItems` orders by. */
type Ordered = Pick<TimelineTrackItem<unknown>, "startTime" | "endTime" | "id">;

/** Start, then end, then id — a total order, since ids are unique within a timeline. */
export function compareItems(a: Ordered, b: Ordered): number {
  return (
    a.startTime - b.startTime ||
    a.endTime - b.endTime ||
    a.id.localeCompare(b.id)
  );
}

/** `-1` steps to an earlier item, `1` to a later one. */
export type StepDirection = -1 | 1;

/**
 * Where a step starts: the selected item, or an instant (ms since the epoch)
 * when nothing is selected, such as the centre of the view or a window's edge.
 */
export type StepOrigin<T> = TimelineTrackItem<T> | number;

/**
 * The item after `from` (direction `1`) or before it (`-1`) in `sorted`, which
 * must be in `compareItems` order; `null` past either end, so a step there is
 * a no-op rather than a wrap. `from` need not be in `sorted`: a selection the
 * user has panned out of view keeps its place in the order. From an instant,
 * `1` finds the first item starting at or after it and `-1` the last item
 * starting before it. Takes O(log n) comparisons, so it is cheap enough to
 * run on every render.
 */
export function findNeighbor<T>(
  sorted: readonly TimelineTrackItem<T>[],
  from: StepOrigin<T>,
  direction: StepDirection,
): TimelineTrackItem<T> | null {
  // An instant sorts as a zero-length item with the empty id, which comes
  // before every item starting at that instant.
  const anchor: Ordered =
    typeof from === "number"
      ? { startTime: from, endTime: from, id: "" }
      : from;
  // Ends at the first index sorting after `anchor` (direction `1`) or not
  // before it (`-1`); for `-1` the answer is the item just before that index.
  let low = 0;
  let high = sorted.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    const order = compareItems(sorted[middle], anchor);
    if (order < 0 || (direction === 1 && order === 0)) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  const index = direction === 1 ? low : low - 1;
  return sorted[index] ?? null;
}

/**
 * Overlapping items go on separate tracks. Greedy first-fit on start-sorted
 * items — interval partitioning, which yields the minimum number of tracks —
 * so ~95 % of the time everything sits on one track. Depends on the data
 * only, never on zoom: compute once per items change.
 */
export function assignTracks<T>(
  sorted: readonly TimelineTrackItem<T>[],
): TrackAssignment {
  const trackEnds: number[] = [];
  const trackById = new Map<string, number>();
  for (const item of sorted) {
    let track = trackEnds.findIndex((end) => end <= item.startTime);
    if (track === -1) {
      track = trackEnds.length;
      trackEnds.push(item.endTime);
    } else {
      trackEnds[track] = item.endTime;
    }
    trackById.set(item.id, track);
  }
  return { trackById, trackCount: Math.max(1, trackEnds.length) };
}

/**
 * The pixel-derived thresholds `placeClips` needs, expressed as fractions of
 * the window so they can be compared with clip positions. The measured
 * container width enters the layout only through here.
 */
export interface ClipThresholds {
  /** A clip's box is never narrower than this; also the merge margin. */
  minClip: number;
  /** A cluster badge is never narrower than this. */
  minCluster: number;
}

export const MIN_CLIP_PX = 6;
export const MIN_CLUSTER_PX = 24;

export function clipThresholds(widthPx: number): ClipThresholds {
  return {
    minClip: MIN_CLIP_PX / widthPx,
    minCluster: MIN_CLUSTER_PX / widthPx,
  };
}

export interface TrackLayout<T> {
  /** Draw order; the selected item's clip comes last so it paints on top. */
  clips: readonly TrackClip<T>[];
  /** The selected item's clip, when it is in the window. */
  selected: Clip<T> | null;
  /** Every item intersecting the window, in start order. */
  visible: readonly TimelineTrackItem<T>[];
  trackCount: number;
}

interface OpenCluster<T> {
  items: TimelineTrackItem<T>[];
  x0: number;
  x1: number;
}

/**
 * Place items on their tracks as clips for one window, merging items whose
 * boxes would overlap into clusters. Positions are fractions of the window.
 *
 * A clip's box is at least `minClip` wide, and a clip joins the open cluster
 * on its track when its left edge falls inside that cluster's box — the
 * minimum width is the merge margin, so there is one threshold, not two. A
 * cluster's box grows to the badge's `minCluster` width, so no bar is drawn
 * under a badge.
 * Merging is transitive (measured to the cluster's right edge): a chain of
 * near-touching items becomes one cluster however long it gets, which at that
 * zoom is the right message, and zooming in splits it. Overlapping items never
 * cluster with each other, because `assignTracks` already put them on
 * different tracks. The selected item is always its own clip.
 */
export function placeClips<T>(
  sorted: readonly TimelineTrackItem<T>[],
  trackById: TrackById,
  window: TimeWindow,
  selectedId: string | null,
  thresholds: ClipThresholds,
): TrackLayout<T> {
  const span = spanOf(window);
  const clips: TrackClip<T>[] = [];
  const visible: TimelineTrackItem<T>[] = [];
  const open: (OpenCluster<T> | null)[] = [];
  let selected: Clip<T> | null = null;
  let trackCount = 1;

  const flush = (track: number) => {
    const cluster = open[track];
    if (!cluster) {
      return;
    }
    open[track] = null;
    if (cluster.items.length === 1) {
      const item = cluster.items[0];
      clips.push({
        kind: "clip",
        item,
        track,
        left: cluster.x0,
        width: cluster.x1 - cluster.x0,
      });
      return;
    }
    let endTime = cluster.items[0].endTime;
    for (const item of cluster.items) {
      endTime = Math.max(endTime, item.endTime);
    }
    clips.push({
      kind: "cluster",
      id: `${track}:${cluster.items[0].id}`,
      items: cluster.items,
      startTime: cluster.items[0].startTime,
      endTime,
      track,
      left: cluster.x0,
      width: Math.max(cluster.x1 - cluster.x0, thresholds.minCluster),
    });
  };

  if (span <= 0) {
    return { clips, selected, visible, trackCount };
  }

  // Items that start past the window's right edge (plus the min clip width,
  // so a bar just outside still counts) can be skipped — the input is sorted.
  const limit = window.end + thresholds.minClip * span;
  for (const item of sorted) {
    if (item.startTime > limit) {
      break;
    }
    const x0 = (item.startTime - window.start) / span;
    const x1 = Math.max(
      x0 + thresholds.minClip,
      (item.endTime - window.start) / span,
    );
    if (x1 < 0) {
      continue;
    }
    if (item.endTime >= window.start && item.startTime <= window.end) {
      visible.push(item);
    }
    const track = trackById.get(item.id) ?? 0;
    trackCount = Math.max(trackCount, track + 1);
    if (item.id === selectedId) {
      selected = { kind: "clip", item, track, left: x0, width: x1 - x0 };
      continue;
    }
    const cluster = open[track];
    if (cluster && x0 < cluster.x1) {
      cluster.items.push(item);
      cluster.x1 = Math.max(cluster.x1, x1, cluster.x0 + thresholds.minCluster);
    } else {
      flush(track);
      open[track] = { items: [item], x0, x1 };
    }
  }
  for (let track = 0; track < open.length; track++) {
    flush(track);
  }
  if (selected) {
    clips.push(selected);
  }
  return { clips, selected, visible, trackCount };
}

export interface Interval {
  start: number;
  end: number;
}

/** Merge overlapping or touching items into disjoint intervals, in order. */
export function unionIntervals<T>(
  sorted: readonly TimelineTrackItem<T>[],
): Interval[] {
  const out: Interval[] = [];
  for (const item of sorted) {
    const last = out[out.length - 1];
    if (last && item.startTime <= last.end) {
      last.end = Math.max(last.end, item.endTime);
    } else {
      out.push({ start: item.startTime, end: item.endTime });
    }
  }
  return out;
}

export interface Gap extends Interval {
  /** True when the gap touches the window's edge, so its true length is unknown. */
  edge: boolean;
}

/** The idle stretches inside `window` between the given active intervals. */
export function computeGaps(
  union: readonly Interval[],
  window: TimeWindow,
): Gap[] {
  const gaps: Gap[] = [];
  let cursor = window.start;
  for (const interval of union) {
    if (interval.start > cursor) {
      gaps.push({
        start: cursor,
        end: Math.min(interval.start, window.end),
        edge: cursor === window.start,
      });
    }
    cursor = Math.max(cursor, interval.end);
    if (cursor >= window.end) {
      break;
    }
  }
  if (cursor < window.end) {
    gaps.push({ start: cursor, end: window.end, edge: true });
  }
  return gaps;
}

/**
 * Whichever of `earlier` (the last item before an empty `window`) and `later`
 * (the first after it) has its facing edge closer to the window; `earlier` on
 * a tie. When one is `null` the other wins; `null` when both are.
 */
export function nearestTo<T>(
  window: TimeWindow,
  earlier: TimelineTrackItem<T> | null,
  later: TimelineTrackItem<T> | null,
): TimelineTrackItem<T> | null {
  if (earlier === null || later === null) {
    return earlier ?? later;
  }
  return window.start - earlier.endTime <= later.startTime - window.end
    ? earlier
    : later;
}

/** True when the window shows no items although some exist. */
export function noneInSpan(visibleCount: number, totalCount: number): boolean {
  return visibleCount === 0 && totalCount > 0;
}

/**
 * The window that shows `item` when the current one does not, or `null` when
 * it already does. "Shows" means the item lies inside the window, or the
 * window lies inside the item — a long item the user has zoomed into is not
 * jumped away from. A partially visible item is re-centred. The span is kept
 * and clamped to the extent; an item longer than the span is fitted instead.
 */
export function revealWindow(
  item: Pick<TimelineTrackItem<unknown>, "startTime" | "endTime">,
  window: TimeWindow,
  extent: TimeWindow,
): TimeWindow | null {
  const inside = item.startTime >= window.start && item.endTime <= window.end;
  const covers = item.startTime <= window.start && item.endTime >= window.end;
  if (inside || covers) {
    return null;
  }
  const span = spanOf(window);
  if (item.endTime - item.startTime > span) {
    return clampWindow(fitWindow(item.startTime, item.endTime), extent);
  }
  const center = (item.startTime + item.endTime) / 2;
  return clampWindow(
    { start: center - span / 2, end: center + span / 2 },
    extent,
  );
}
